import { lazy, Suspense, type ComponentType } from 'react';
import {
  createBrowserRouter,
  isRouteErrorResponse,
  useRouteError,
  Link,
  type RouteObject,
} from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { PublicLayout } from './public/PublicLayout';
import { HomePage } from './public/pages/HomePage';
import { WorkPage } from './public/pages/WorkPage';
import { ProjectPage } from './public/pages/ProjectPage';
import { NewsletterConfirmPage, UnsubscribePage } from './public/pages/NewsletterPages';
import { NotFoundPage } from './public/pages/states';
import { useLocale } from './public/i18n';

/** Admin is code-split: public visitors never download dashboard or editor code. */
function adminPage<M>(loader: () => Promise<M>, name: keyof M & string) {
  const C = lazy(() => loader().then((m) => ({ default: m[name] as ComponentType })));
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[50vh] items-center justify-center">
          <Loader2 className="size-5 animate-spin text-primary" />
        </div>
      }
    >
      <C />
    </Suspense>
  );
}

const A = () => import('./admin');

function RouteError() {
  const err = useRouteError();
  const { t, to } = useLocale();
  const notFound = isRouteErrorResponse(err) && err.status === 404;
  return (
    <div dir={t.dir} className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center px-6">
      <p className="text-6xl font-black tracking-tight text-primary">{notFound ? '404' : 'Oops'}</p>
      <h1 className="mt-4 text-2xl font-bold">
        {notFound ? t.states.notFoundTitle : t.states.broke}
      </h1>
      <p className="mt-2 text-sm text-muted">{notFound ? t.states.notFound : t.states.brokeText}</p>
      <div className="mt-6 flex gap-2">
        <button
          onClick={() => window.location.reload()}
          className="h-10 rounded-lg bg-ink px-4 text-sm font-semibold text-white"
        >
          {t.states.reload}
        </button>
        <Link
          to={to('/')}
          className="inline-flex h-10 items-center rounded-lg border border-line px-4 text-sm"
        >
          {t.home}
        </Link>
      </div>
    </div>
  );
}

/** Public pages, mounted at "/" (English) and again at "/ar" (Arabic, right-to-left). */
const publicPages: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: 'work', element: <WorkPage /> },
  { path: 'work/:slug', element: <ProjectPage /> },
  { path: 'newsletter/confirm', element: <NewsletterConfirmPage /> },
  { path: 'newsletter/unsubscribe', element: <UnsubscribePage /> },
  { path: '*', element: <NotFoundPage /> },
];

export const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    errorElement: <RouteError />,
    children: [{ path: 'ar', children: publicPages }, ...publicPages],
  },
  { path: '/admin/login', element: adminPage(A, 'LoginPage'), errorElement: <RouteError /> },
  {
    path: '/admin/forgot-password',
    element: adminPage(A, 'ForgotPasswordPage'),
    errorElement: <RouteError />,
  },
  {
    path: '/admin/reset-password',
    element: adminPage(A, 'ResetPasswordPage'),
    errorElement: <RouteError />,
  },
  {
    path: '/admin',
    element: adminPage(A, 'AdminRoot'),
    errorElement: <RouteError />,
    children: [
      { index: true, element: adminPage(A, 'OverviewPage') },
      { path: 'projects', element: adminPage(A, 'ProjectsPage') },
      { path: 'projects/new', element: adminPage(A, 'ProjectEditorPage') },
      { path: 'projects/:id', element: adminPage(A, 'ProjectEditorPage') },
      { path: 'projects/:id/preview', element: adminPage(A, 'ProjectPreviewPage') },
      { path: 'categories', element: adminPage(A, 'CategoriesPage') },
      { path: 'testimonials', element: adminPage(A, 'TestimonialsPage') },
      { path: 'statistics', element: adminPage(A, 'StatisticsPage') },
      { path: 'services', element: adminPage(A, 'ServicesPage') },
      { path: 'process', element: adminPage(A, 'ProcessPage') },
      { path: 'faq', element: adminPage(A, 'FaqPage') },
      { path: 'clients', element: adminPage(A, 'ClientsPage') },
      { path: 'website', element: adminPage(A, 'SiteContentPage') },
      { path: 'socials', element: adminPage(A, 'SocialLinksPage') },
      { path: 'media', element: adminPage(A, 'MediaPage') },
      { path: 'newsletter', element: adminPage(A, 'CampaignsPage') },
      { path: 'newsletter/campaigns/:id', element: adminPage(A, 'CampaignEditorPage') },
      { path: 'newsletter/campaigns/:id/report', element: adminPage(A, 'CampaignReportPage') },
      { path: 'newsletter/subscribers', element: adminPage(A, 'SubscribersPage') },
      { path: 'newsletter/templates', element: adminPage(A, 'TemplatesPage') },
      { path: 'newsletter/templates/:id', element: adminPage(A, 'TemplateEditorPage') },
      { path: 'account', element: adminPage(A, 'AccountPage') },
      { path: '*', element: adminPage(A, 'AdminNotFound') },
    ],
  },
]);
